import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Button,
  Card,
  AlertIcon,
  Flex,
  Modal,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import type { QualifiedGroupBase, QualifiedGroupType } from '@iheartjane/types';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import { ScheduleCard } from '@jane/business-admin/components/shared/schedule/ScheduleCard';
import {
  DEFAULT_SCHEDULE,
  formatSchedule,
  parseSchedule,
} from '@jane/business-admin/components/shared/schedule/scheduleHelpers';
import { convert12HourTimeTo24HourTime } from '@jane/business-admin/components/shared/schedule/ScheduleTimeValidation';
import { ConditionsCard } from '@jane/business-admin/components/shared/specials/modal/conditions/ConditionsCard';
import type { SpecialFormDataBase } from '@jane/business-admin/components/shared/specials/modal/form';
import {
  SHOW_PROMO_CODE_SPECIAL_TYPES,
  getBundleDisplay,
  getConditionGroupCount,
  getRuleSettingsFormState,
  getRules,
  getSpendingThresholdDisplay,
  parseRulesFromApplyTo,
  useTriggerDuplicateValidation,
} from '@jane/business-admin/components/shared/specials/modal/form';
import { ImagesCard } from '@jane/business-admin/components/shared/specials/modal/ImagesCard';
import { MultipleConditions } from '@jane/business-admin/components/shared/specials/modal/multipleConditions/multipleConditions';
import { PromoCodeCard } from '@jane/business-admin/components/shared/specials/modal/PromoCodeCard';
import { ArchiveWithConfirm } from '@jane/business-admin/components/specials/ArchiveWithConfirm';
import {
  useArchiveGlobalSpecial,
  useCreateGlobalSpecial,
  useFetchGlobalSpecial,
  useUpdateGlobalSpecial,
} from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useHasPermissions,
  useMutationStatusToasts,
} from '@jane/business-admin/hooks';
import { GlobalSpecialsModalProvider } from '@jane/business-admin/providers/GlobalSpecialsModalProvider';
import { SpecialStatus } from '@jane/business-admin/types';
import type {
  GlobalSpecialStore,
  GlobalSpecialV2,
} from '@jane/business-admin/types';
import {
  EventNames,
  MODAL_CARD_WIDTH,
  ModalNames,
  buildSpecialPhotoAttributes,
  businessPaths,
  parseValidationErrors,
  track,
} from '@jane/business-admin/util';
import type { ValueOf } from '@jane/business-admin/util';
import type { ConditionEditorKey } from '@jane/shared-b2b/components';
import {
  convertConditionSetsToRuleSets,
  convertRulesToConditionSets,
} from '@jane/shared-b2b/components';
import { Permission } from '@jane/shared/auth';

import { DetailsCard } from './DetailsCard';
import { StoreSelectorCard } from './StoreSelectorCard';

const FORM_ERROR_NAME = 'global-special-edit-error';

/**
 * Exported for testing
 */
export type StoreMap = {
  [storeId: number]: GlobalSpecialStore;
};

type SpecialFormData = SpecialFormDataBase &
  Omit<
    GlobalSpecialV2,
    | 'special_type'
    | 'photo'
    | 'store_specific'
    | 'detail_summary'
    | 'id'
    | 'title'
    | 'hidden_from_page'
    | 'hidden_from_row'
    | 'stores'
  > & {
    stores: StoreMap;
  };

const Mode = {
  create: 'create',
  update: 'update',
  duplicate: 'duplicate',
} as const;

type Mode = ValueOf<typeof Mode>;

const modeToModalHeaderTitleMap: Record<Mode, string> = {
  [Mode.create]: 'Create Global Special',
  [Mode.duplicate]: 'Duplicate Global Special',
  [Mode.update]: 'Edit Global Special',
};

const modeToGenericErrorStringMap: Record<Mode, string> = {
  [Mode.create]: 'creating',
  [Mode.update]: 'updating',
  [Mode.duplicate]: 'duplicating',
};

const modeToSuccessMessageSuffixMap: Record<Mode, string> = {
  [Mode.create]: 'created',
  [Mode.duplicate]: 'created',
  [Mode.update]: 'updated',
};

const modeToActionMap: Record<Mode, 'create' | 'duplicate' | 'update'> = {
  [Mode.create]: 'create',
  [Mode.duplicate]: 'duplicate',
  [Mode.update]: 'update',
};

export const GlobalSpecialsModal = () => {
  const multiConditionSpecials = useFlag(FLAGS.c3MultipleConditionSpecials);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [headerError, setHeaderError] = useState<string>();
  const { special_id: specialId = '' } = useParams<'special_id'>();
  const previousStatus = localStorage.getItem('currentSpecialStatus');
  const specialIdIsCreate = specialId === 'create';

  const { pathname } = useLocation();
  const pathIncludesDuplicate = pathname.includes('duplicate');

  const mode = pathIncludesDuplicate
    ? Mode.duplicate
    : specialIdIsCreate
      ? Mode.create
      : Mode.update;

  const genericErrorMessage = `Error ${modeToGenericErrorStringMap[mode]} special. Please try again.`;

  const navigate = useNavigate();
  const toast = useToast();

  const { data: special, isFetching: specialLoading } = useFetchGlobalSpecial(
    specialId,
    mode === Mode.create
  );

  const isArchived = special?.archived || false;
  const conditionSets = convertRulesToConditionSets(
    special?.rules,
    special?.special_type
  );
  // // Navigate and show error if there is no special when loading is finished
  useEffect(() => {
    if (mode !== Mode.create && !specialLoading && !special) {
      toast.add({
        label: 'Special could not be found.',
        variant: 'error',
      });
      navigate(
        businessPaths.specials(
          (previousStatus as SpecialStatus) || SpecialStatus.live
        )
      );
      setIsOpen(false);
    }
  }, [specialLoading, special]);

  const {
    mutateAsync: createSpecial,
    isPending: isCreating,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
  } = useCreateGlobalSpecial();
  const {
    mutateAsync: updateSpecial,
    isPending: isUpdating,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
  } = useUpdateGlobalSpecial(specialId);
  const {
    mutateAsync: archiveSpecial,
    isPending: archiveSpecialLoading,
    isSuccess: archiveSpecialSuccess,
    isError: archiveSpecialError,
  } = useArchiveGlobalSpecial();

  useMutationStatusToasts({
    isMutating: archiveSpecialLoading,
    isSuccess: archiveSpecialSuccess,
    isError: archiveSpecialError,
    successMessage: 'Special successfully archived',
    errorMessage: 'Error archiving special',
  });

  useEffect(() => {
    if (specialId) setIsOpen(true);
  }, [specialId]);

  const userCanEditSpecials = useHasPermissions([Permission.EditSpecials]);
  const catchSubmitErrors = useCatchErrorsWithManager(genericErrorMessage);

  const weightValuesEnabled = useFlag(FLAGS.scWeightValuesFrontend);

  const parsedSchedule = parseSchedule(
    {
      schedule: special?.schedule,
      startTime: special?.start_time,
      endTime: special?.end_time,
    },
    mode === Mode.create
  );

  const formatStoresForFormState = (stores: GlobalSpecialStore[]) => {
    if (!Array.isArray(stores)) return stores;
    if (!stores.length) return {};
    const storesObject: StoreMap = {};
    stores.forEach(
      (store: GlobalSpecialStore) => (storesObject[store.id as number] = store)
    );
    return storesObject;
  };

  const filterAndFormatStoresForFormSubmit = (
    stores: { [storeId: number]: GlobalSpecialStore } | GlobalSpecialStore[]
  ): GlobalSpecialStore[] => {
    return Object.values(stores).filter(
      (store): store is GlobalSpecialStore => !!store?.id && !!store?.enabled
    );
  };

  const isCreateMode = mode === Mode.create;
  const fetchedSpecialValues: SpecialFormData = {
    conditionSets,
    display: {
      ...getRules(isCreateMode, special),
      bundle: getBundleDisplay(isCreateMode, special),
      promo_code_enabled: !!special?.promo_code,
      spending_threshold: getSpendingThresholdDisplay(isCreateMode, special),
    },
    special_type: special?.special_type || '',
    name: special?.title || '',
    custom_badge: special?.custom_badge || '',
    promo_code: special?.promo_code,
    multiple_use_promo_code:
      (special?.promo_code && special?.multiple_use_promo_code) || false,
    promo_code_max_number_of_uses:
      special?.promo_code_max_number_of_uses || undefined,
    ruleSettings: getRuleSettingsFormState(special),
    discount_dollar_amount: special?.discount_dollar_amount,
    discount_percent: special?.discount_percent,
    discount_target_price: special?.discount_target_price,
    discount_type: special?.discount_type || '',
    enabled: !!special?.enabled,
    description: special?.description || '',
    reservation_modes: special?.reservation_modes,
    stacking_setting: special?.stacking_setting || 'yes',
    stores: formatStoresForFormState(special?.stores || []),
    terms: special?.terms || '',
    photo:
      special?.photo && special.photo['urls']['original']
        ? [special.photo['urls']['original']]
        : undefined,
    hidden_from: !!special?.hidden_from_row && !!special?.hidden_from_page,
    rules: special?.rules || {},
    rules_entities: special?.rules_entities || {},
    ...parsedSchedule,
  };

  const formMethods = useForm<SpecialFormData>({
    defaultValues: {
      conditionSets,
      display: {
        ...getRules(isCreateMode, special),
        bundle: getBundleDisplay(isCreateMode, special),
        promo_code_enabled: isCreateMode ? false : !!special?.promo_code,
        spending_threshold: getSpendingThresholdDisplay(isCreateMode, special),
      },
      special_type: isCreateMode ? '' : special?.special_type || '',
      name: isCreateMode ? '' : special?.title || '',
      custom_badge: isCreateMode ? '' : special?.custom_badge || '',
      promo_code: isCreateMode ? '' : special?.promo_code,
      multiple_use_promo_code: isCreateMode
        ? false
        : !!(special?.promo_code && special?.multiple_use_promo_code),
      promo_code_max_number_of_uses: isCreateMode
        ? undefined
        : special?.promo_code_max_number_of_uses || undefined,
      ruleSettings: isCreateMode
        ? {
            cart_total: {
              threshold: 0,
            },
            bulk_pricing: {
              target_weight: 'ounce',
              max_applications_per_cart: 1,
            },
            qualified_group: {
              type: 'senior',
              required_age: 65,
              verification_type: 'photo',
            },
            bundle: {
              settings: {
                allow_discounts_on_required_products: false, // Allow qualifying products to be discounted
              },
              dependent: {
                max_number_of_discounted_products: 1, // Max products discounted
              },
              independent: {
                threshold_number_of_items_in_cart: 1, // Minimum products requirement
              },
              max_applications_per_cart: 1, // Max uses per order
            },
          }
        : getRuleSettingsFormState(special),
      discount_type: isCreateMode ? 'percent' : special?.discount_type || '',
      discount_dollar_amount: isCreateMode
        ? 0
        : special?.discount_dollar_amount || 0,
      discount_percent: isCreateMode ? 0 : special?.discount_percent || 0,
      discount_target_price: isCreateMode
        ? 0
        : special?.discount_target_price || 0,
      enabled: isCreateMode ? false : !!special?.enabled,
      description: isCreateMode ? '' : special?.description || '',
      reservation_modes: isCreateMode
        ? {
            delivery: true,
            pickup: true,
          }
        : {
            delivery: !!special?.reservation_modes?.delivery,
            pickup: !!special?.reservation_modes?.pickup,
          },
      terms: isCreateMode ? '' : special?.terms || '',
      stacking_setting: isCreateMode ? 'yes' : special?.stacking_setting,
      stores: isCreateMode
        ? []
        : formatStoresForFormState(special?.stores || []),
      photo: isCreateMode
        ? undefined
        : special?.photo && special.photo['urls']['original']
          ? [special.photo['urls']['original']]
          : undefined,
      hidden_from: !!special?.hidden_from_row && !!special?.hidden_from_page,
      rules: isCreateMode ? {} : special?.rules,
      ...parsedSchedule,
    },
    values: isCreateMode ? undefined : fetchedSpecialValues,
  });

  const isMutating = isCreating || isUpdating;

  useMutationStatusToasts({
    isMutating: isCreating || isUpdating,
    isSuccess: isCreateSuccess || isUpdateSuccess,
    isError: isCreateError || isUpdateError,
    successMessage: `Special successfully ${modeToSuccessMessageSuffixMap[mode]}`,
    errorMessage: genericErrorMessage,
  });

  const {
    formState: { dirtyFields, isDirty },
    trigger,
    watch,
  } = formMethods;

  const specialTypeWatch = watch('special_type');
  const requiredProductsCount = watch(
    'display.bundle.threshold_number_of_items_in_cart'
  );
  const requiredSpendAmount = watch(
    'display.spending_threshold.cart_threshold'
  );
  const discountedProductsCount = watch(
    `display.${
      specialTypeWatch as 'bundle' | 'spending_threshold'
    }.max_number_of_discounted_products`
  );

  const getSubheaders = useMemo(() => {
    if (!['bundle', 'spending_threshold'].includes(specialTypeWatch)) {
      return {
        requiredSubhead: undefined,
        discountedSubhead: undefined,
      };
    }

    let requiredSubhead;
    if (requiredProductsCount && requiredProductsCount > 0) {
      requiredSubhead = `${requiredProductsCount} ${pluralize({
        noun: 'product',
        number: requiredProductsCount,
        returnValue: 'word',
      })} required to unlock Special.`;
    }

    if (requiredSpendAmount && requiredSpendAmount > 0) {
      requiredSubhead = `${formatCurrency(
        requiredSpendAmount
      )} required to unlock Special.`;
    }

    let discountedSubhead;
    if (discountedProductsCount && discountedProductsCount > 0) {
      discountedSubhead = `${discountedProductsCount} ${pluralize({
        noun: 'product',
        number: discountedProductsCount,
        returnValue: 'word',
      })} maximum to discount.`;
    }

    return {
      requiredSubhead,
      discountedSubhead,
    };
  }, [requiredProductsCount, discountedProductsCount, specialTypeWatch]);

  const hasUneditableStores =
    special?.stores?.some(({ can_edit }) => !can_edit) || false;
  const isEditable = userCanEditSpecials !== false;

  const specialIsLoading = mode !== Mode.create && specialLoading;

  const hasDuplicationErrors = useTriggerDuplicateValidation(
    ['validate_global_promo_code'],
    specialIsLoading,
    mode === Mode.duplicate,
    trigger
  );

  const onSubmit = (formData: SpecialFormData) => {
    setHeaderError(undefined);
    const specialType = formData['special_type'];

    // the special type select is required, so this is unreachable from the UI
    if (specialType === '') {
      setHeaderError('Must choose a valid special type');
      return;
    }

    const includePromoCode =
      SHOW_PROMO_CODE_SPECIAL_TYPES.includes(specialType) &&
      formData['display']['promo_code_enabled'] === true;
    const includeScheduleOverride = formData['has_schedule_overrides'] === true;

    const ruleSetsFor = (key: ConditionEditorKey) =>
      convertConditionSetsToRuleSets(formData.conditionSets[key]);

    const {
      photo: __omitted2,
      start_date: __omitted3,
      end_date: __omitted4,
      start_time: __omitted5,
      end_time: __omitted6,
      ruleSettings: __omitted7,
      schedule: __omitted8,
      hidden_from: __omitted9,
      conditionSets: __omitted10,
      display,
      ...desiredFormData
    } = formData;
    const schedule = includeScheduleOverride
      ? formatSchedule(formData['schedule'])
      : DEFAULT_SCHEDULE;

    const photoAttributes = buildSpecialPhotoAttributes({
      photoDirty: !!dirtyFields['photo'],
      photoId: special?.photo?.id,
      currentPhoto: formData['photo']?.[0],
    });

    const requestData = {
      ...desiredFormData,
      special_type: specialType,
      stores: filterAndFormatStoresForFormSubmit(formData['stores']),
      promo_code: includePromoCode ? formData['promo_code'] : null,
      multiple_use_promo_code: includePromoCode
        ? !!formData['multiple_use_promo_code']
        : false,
      promo_code_max_number_of_uses: includePromoCode
        ? formData['promo_code_max_number_of_uses']
        : null,
      title: formData['name'],
      discount_dollar_amount: formData['discount_dollar_amount'] || 0,
      discount_percent: formData['discount_percent'] || 0,
      discount_target_price: formData['discount_target_price'] || 0,
      end_date: formData['end_date'] || null,
      enabled_date_end: convert12HourTimeTo24HourTime(formData['end_time']),
      start_date: formData['start_date'] || null,
      enabled_date_start: convert12HourTimeTo24HourTime(formData['start_time']),
      use_store_close_time_for_end: !!formData['use_store_close_time'],
      is_duplicate: pathIncludesDuplicate,
      ...(photoAttributes ? { photo_attributes: photoAttributes } : {}),
      hidden_from_row: !!formData['hidden_from'],
      hidden_from_page: !!formData['hidden_from'],
      // The global-specials endpoint expects the schedule day fields flat at the
      // top level (unlike store specials, which nest them under `schedule`).
      ...schedule,
    };

    if (specialType === 'bundle') {
      // If special is bundle, convert temporary rules to conditions so bundle special can be updated

      const isCopyingDiscountedRules =
        display?.bundle?.settings?.copy_conditions_for_discounted_products;

      const requiredRules = multiConditionSpecials
        ? { rule_sets: ruleSetsFor('qualifier') }
        : parseRulesFromApplyTo(
            display.required_rules?.rules || {},
            display.required_rules?.apply_to || 'CONDITIONS',
            weightValuesEnabled,
            'bundle'
          );

      const discountedRules = isCopyingDiscountedRules
        ? { ...requiredRules }
        : multiConditionSpecials
          ? { rule_sets: ruleSetsFor('discountable') }
          : parseRulesFromApplyTo(
              display.discounted_rules?.rules || {},
              display.discounted_rules?.apply_to || 'CONDITIONS',
              weightValuesEnabled,
              'bundle'
            );

      const bundleSettings = {
        ...formData.display.bundle.settings,
        apply_target_price_per_unit:
          formData['discount_type'] === 'target_price'
            ? (formData.display.bundle.settings?.apply_target_price_per_unit ??
              false)
            : false,
      };

      requestData['rules'] = {
        qualifier: [requiredRules],
        discountable: [discountedRules],
        settings: {
          allow_discounts_on_required_products:
            bundleSettings.allow_discounts_on_required_products,
          threshold_number_of_items_in_cart:
            formData.display.bundle.threshold_number_of_items_in_cart || 0,
          max_number_of_discounted_products:
            formData.display.bundle.max_number_of_discounted_products || 0,
          max_applications_per_cart:
            formData.display.bundle.max_applications_per_cart || 0,
          ...(bundleSettings.exclude_product_specials_from_bundle_qualifiers !==
            undefined && {
            exclude_product_specials_from_bundle_qualifiers:
              bundleSettings.exclude_product_specials_from_bundle_qualifiers,
          }),
          ...(bundleSettings.copy_conditions_for_discounted_products !==
            undefined && {
            copy_conditions_for_discounted_products:
              bundleSettings.copy_conditions_for_discounted_products,
          }),
          ...(bundleSettings.apply_target_price_per_unit !== undefined && {
            apply_target_price_per_unit:
              bundleSettings.apply_target_price_per_unit,
          }),
        },
      };
    } else if (specialType === 'bulk_pricing') {
      const rules = multiConditionSpecials
        ? { rule_sets: ruleSetsFor('default') }
        : parseRulesFromApplyTo(
            display.default_rules?.rules || {},
            display.default_rules?.apply_to || 'CONDITIONS',
            weightValuesEnabled,
            specialType
          );
      const bulkTargetWeight =
        formData.ruleSettings?.bulk_pricing?.target_weight || 'each';
      const bulkMaxApplications =
        formData.ruleSettings?.bulk_pricing?.max_applications_per_cart || 0;

      requestData['rules'] = {
        ...rules,
        settings: {
          target_weight: bulkTargetWeight,
          max_applications_per_cart: bulkMaxApplications,
        },
      };
    } else if (specialType === 'cart_total') {
      const rawThreshold = formData.ruleSettings.cart_total?.threshold || 0;
      const threshold =
        typeof rawThreshold === 'string'
          ? parseFloat(rawThreshold)
          : rawThreshold;

      requestData['rules'] = {
        ...(multiConditionSpecials
          ? { rule_sets: ruleSetsFor('default') }
          : parseRulesFromApplyTo(
              display.default_rules?.rules || {},
              display.default_rules?.apply_to || 'CONDITIONS',
              weightValuesEnabled,
              'cart_total'
            )),
        settings: {
          cart_threshold: threshold,
        },
      };
    } else if (specialType === 'spending_threshold') {
      const isCopyingDiscountedRules =
        display?.spending_threshold?.copy_conditions_for_discounted_products;

      const requiredRules = multiConditionSpecials
        ? { rule_sets: ruleSetsFor('qualifier') }
        : parseRulesFromApplyTo(
            display.required_rules?.rules || {},
            display.required_rules?.apply_to || 'CONDITIONS',
            weightValuesEnabled,
            'spending_threshold'
          );

      const discountedRules = isCopyingDiscountedRules
        ? { ...requiredRules }
        : multiConditionSpecials
          ? { rule_sets: ruleSetsFor('discountable') }
          : parseRulesFromApplyTo(
              display.discounted_rules?.rules || {},
              display.discounted_rules?.apply_to || 'CONDITIONS',
              weightValuesEnabled,
              'spending_threshold'
            );

      requestData['rules'] = {
        discountable: discountedRules,
        qualifier: requiredRules,
        settings: formData.display.spending_threshold,
      };
    } else if (specialType === 'qualified_group') {
      const { required_age, type, verification_type } =
        formData.ruleSettings?.qualified_group || {};

      const base: QualifiedGroupBase = {
        type: (type as QualifiedGroupType) || undefined,
        required_age: required_age || undefined,
        verification_type:
          type === 'medical' ? (verification_type ?? 'photo') : undefined,
      };

      requestData['rules'] = { base };
    } else {
      requestData['rules'] = multiConditionSpecials
        ? { rule_sets: ruleSetsFor('default') }
        : parseRulesFromApplyTo(
            display.default_rules?.rules || {},
            display.default_rules?.apply_to || 'CONDITIONS',
            weightValuesEnabled,
            'product'
          );
    }

    const submitMethod = () => {
      track({
        action: modeToActionMap[mode],
        event: EventNames.EditedSpecial,
        changed_attributes: Object.keys(dirtyFields),
        ...(multiConditionSpecials && {
          condition_group_count: getConditionGroupCount({
            conditionSets: formData.conditionSets,
            display: formData.display,
            specialType,
          }),
        }),
      });

      return mode === Mode.update
        ? updateSpecial(requestData)
        : createSpecial(requestData);
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: () => {
        setHeaderError(genericErrorMessage);
        throw new Error(genericErrorMessage);
      },
    });
  };

  const onArchive = () => {
    catchSubmitErrors({
      submitMethod: () => archiveSpecial({ specialId: String(specialId) }),
      requestData: {},
      // Error handled by toasts
      callback: () => null,
      onValidationError: () => null,
    });
  };

  const handleClose = () => {
    navigate(
      businessPaths.specials(
        (previousStatus as SpecialStatus) || SpecialStatus.live
      )
    );
  };

  const isSuccessfulExit =
    isCreateSuccess || isUpdateSuccess || archiveSpecialSuccess;

  useEffect(() => {
    if (!isSuccessfulExit) {
      return;
    }

    handleClose();
  }, [isSuccessfulExit]);

  return (
    <ConfirmWrapperWithTracking
      open={isOpen}
      setOpen={handleClose}
      hasChanges={isDirty}
      variant="full-screen"
      background="grays-ultralight"
      modalName={ModalNames.CreateSpecial}
    >
      <Form.BaseForm
        name="special settings"
        formMethods={formMethods}
        onSubmit={onSubmit}
        formErrorName={FORM_ERROR_NAME}
      >
        <Modal.Header
          title={modeToModalHeaderTitleMap[mode]}
          actions={
            <Flex alignItems="center">
              {headerError && (
                <Flex mr={12}>
                  <AlertIcon color="error" mr={8} />
                  <Typography color="error">{headerError}</Typography>
                </Flex>
              )}
              {isEditable && !specialIsLoading && (
                <>
                  {mode === Mode.update && !hasUneditableStores && (
                    <>
                      {!isArchived && (
                        <ArchiveWithConfirm
                          archiveButton={(onArchiveClick) => (
                            <Button
                              mr={12}
                              onClick={onArchiveClick}
                              label="Archive"
                              variant="secondary"
                              disabled={isMutating}
                            />
                          )}
                          onArchiveSpecial={onArchive}
                        />
                      )}
                      <Button
                        mr={12}
                        label="Duplicate"
                        variant="secondary"
                        to={businessPaths.globalSpecialDuplicate(specialId)}
                      />
                    </>
                  )}
                  {(!isArchived || (isArchived && mode === Mode.duplicate)) && (
                    <Form.SubmitButton
                      label="Publish"
                      variant="primary"
                      data-testid="special-edit-save"
                      disabled={specialIsLoading || isMutating}
                    />
                  )}
                </>
              )}
            </Flex>
          }
        />
        <Modal.Content>
          <Form.ErrorBanner name={FORM_ERROR_NAME} />
          <GlobalSpecialsModalProvider
            hasDuplicationErrors={hasDuplicationErrors}
            isDisabled={hasUneditableStores}
          >
            <Flex alignItems="center" flexDirection="column">
              <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
                <Card.Content>
                  <Flex p={24} flexDirection="column">
                    <DetailsCard
                      isEditable={isEditable && !hasUneditableStores}
                      isLoading={specialIsLoading}
                      isCreateMode={mode === Mode.create}
                      special={special}
                    />
                  </Flex>
                </Card.Content>
              </Card>
              {multiConditionSpecials ? (
                <MultipleConditions
                  defaultCollapsed={mode === Mode.update}
                  specialType={specialTypeWatch}
                />
              ) : (
                <>
                  <ConditionsCard
                    isGlobalSpecial={true}
                    fieldPrefix={
                      ['bundle', 'spending_threshold'].includes(
                        specialTypeWatch
                      )
                        ? 'display.required_rules'
                        : 'display.default_rules'
                    }
                    isLoading={specialIsLoading}
                    headerText={
                      (['bundle', 'spending_threshold'].includes(
                        specialTypeWatch
                      ) &&
                        'Conditions for required products') ||
                      undefined
                    }
                    subHeaderText={getSubheaders['requiredSubhead']}
                  />

                  {['bundle', 'spending_threshold'].includes(
                    specialTypeWatch
                  ) && (
                    <ConditionsCard
                      isGlobalSpecial={true}
                      fieldPrefix="display.discounted_rules"
                      isLoading={specialIsLoading}
                      headerText={
                        (['bundle', 'spending_threshold'].includes(
                          specialTypeWatch
                        ) &&
                          'Conditions for discounted products') ||
                        undefined
                      }
                      subHeaderText={getSubheaders['discountedSubhead']}
                    />
                  )}
                </>
              )}
              <PromoCodeCard
                isDuplicateMode={mode === Mode.duplicate}
                isCreateMode={mode === Mode.create}
                isGlobalSpecial={true}
                isLoading={specialIsLoading}
                specialPromoCode={special?.promo_code}
              />
              <ScheduleCard isLoading={specialIsLoading} mb={16} />
              <ImagesCard isLoading={specialIsLoading} />
              {!specialIsLoading && (
                <StoreSelectorCard specialIsLoading={specialIsLoading} />
              )}
            </Flex>
          </GlobalSpecialsModalProvider>
        </Modal.Content>
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};
